// One-line descriptions of experiments and programs for lists.

import { staircaseTypeLabel } from "../components/ContrastMethod";
import { DEFAULT_APPEARANCE, withStaircaseDefaults, type Eye, type EyeOrder, type ExperimentConfig, type Protocol, type RGB } from "./protocol";

export const EYE_ORDERS: Array<[string, Eye[]]> = [
  ["Right, then left", ["OD", "OS"]], ["Left, then right", ["OS", "OD"]],
  ["Right eye only", ["OD"]], ["Left eye only", ["OS"]], ["Both eyes together", ["OU"]],
  ["Right, left, then both", ["OD", "OS", "OU"]],
];

export const EYE_ORDER_LABEL: Record<EyeOrder, string> = {
  eye_by_eye: "every experiment for one eye, then the next eye",
  step_by_step: "each experiment for every eye, then the next experiment",
};

export const ACUITY_METHODS: Array<[string, string]> = [
  ["quest", "QUEST (Bayesian)"], ["levitt", "Levitt n-down/1-up"],
  ["weighted", "Weighted up/down (Kaernbach)"], ["simple", "1-up/1-down"],
];

const rgbText = (v: RGB) => `(${v.map((c) => +c.toFixed(3)).join(", ")})`;

export function eyesLabel(eyes: Eye[]): string {
  return EYE_ORDERS.find(([, e]) => e.join() === eyes.join())?.[0] ?? eyes.join(", ");
}

export function experimentSummary(e: ExperimentConfig): string {
  const parts: string[] = [];
  if (e.kind === "acuity" && e.acuity) {
    const a = e.acuity;
    parts.push(`${ACUITY_METHODS.find(([k]) => k === a.method)?.[1] ?? a.method}`
      + (a.method === "quest" ? `, 2 × ${a.quest_trials} trials` : ""));
    if (a.catch_rate > 0) parts.push(`${Math.round(a.catch_rate * 100)}% attention checks`);
    if (a.edge_jitter?.enabled) parts.push("edge jitter");
  } else if (e.contrast) {
    const c = withStaircaseDefaults(e.contrast);
    parts.push(c.size_rule === "pelli_robson" ? `letters ${c.pelli_robson_letter_deg}°` : `letters acuity + ${c.acuity_offset_logmar} logMAR`);
    if (c.method === "pelli_robson") parts.push(`chart levels, ${c.pr_group_size} Es per level, pass ${c.pr_pass_correct}`);
    else if (c.method === "staircases") {
      parts.push(`${c.n_staircases} ${staircaseTypeLabel(c.staircase_type)} staircase${c.n_staircases === 1 ? "" : "s"} × ${c.stop_rule === "settled" ? "≤" : ""}${c.trials_per_staircase}`);
      if (c.stagger !== "none") parts.push("staggered");
      if (c.pick === "balanced") parts.push("balanced");
      if (c.catch_rate > 0) parts.push(`${Math.round(c.catch_rate * 100)}% attention checks`);
    } else parts.push("QUEST pair");
    if (c.edge_jitter?.enabled) parts.push("edge jitter");
  }
  const a = e.appearance ?? DEFAULT_APPEARANCE;
  const bg: RGB = a.background_rgb_rel ?? [e.background_rel, e.background_rel, e.background_rel];
  if (bg.some((v) => v !== 1) || a.letter_rgb_rel.some((v) => v !== 0)) {
    parts.push(`letter ${rgbText(a.letter_rgb_rel)} on ${rgbText(bg)}`);
  }
  if (e.kind === "contrast" && a.contrast_mode === "toward_letter") parts.push("toward the letter colour");
  return parts.join("; ");
}

export function programSummary(p: Protocol): string {
  const steps = p.steps.map((s) => `${s.name || s.experiment.kind}${s.times > 1 ? ` ×${s.times}` : ""}`).join(" → ");
  return `${eyesLabel(p.eyes)}: ${steps}; ${(p.viewing_distance_mm / 1000).toFixed(2)} m`;
}
